import React, {useRef} from 'react';
import {FlatList, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {makeStyles} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {useStores} from '../../../stores/use-stores';
import {OffersItemWithMflId} from '../../../transport/types';
import {brandRewardsCopy} from '../../../utils/rewards';
import {visibleScrollbarProps} from '../../../utils/webScrollbar';
import Text from '../../themed/Text';
import TitleLine from '../TitleLine';

import ExclusiveOffersTileCompact from './ExclusiveOffersTileCompact';
import ExclusiveOffersTileLarge from './ExclusiveOffersTileLarge';

/** Hard limit for maximum offers displayed across all variants */
export const MAX_OFFERS_DISPLAY = 30;

type ExclusiveOffersProps = {
  variant?: 'cart';
  onSeeAllPress?: () => void;
};

function ExclusiveOffers({onSeeAllPress, variant}: ExclusiveOffersProps) {
  const {offersStore, cartStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const renderItem = createRenderItem(variant);

  const styles = useStyles({variant});

  const data = offersStore.data || [];

  /**
   * Activated deal should appear first
   * Note: Use slice() before sort() to avoid mutating MobX observable array
   * Limited to MAX_OFFERS_DISPLAY (30) offers
   */
  const isOfferActivated = (offer: OffersItemWithMflId) => cartStore.isOfferApplied(offer.mflId);

  const dataActivatedFirst = data
    .slice()
    .sort((a, b) => {
      if (isOfferActivated(a)) {
        return -1;
      }
      if (isOfferActivated(b)) {
        return 1;
      }
      return 0;
    })
    .slice(0, MAX_OFFERS_DISPLAY);

  const flatListRef = useRef<FlatList>(null);

  useFocusEffect(
    React.useCallback(() => {
      // Code here will run when the screen is focused
      if (flatListRef.current) {
        if (dataActivatedFirst.length > 0) {
          flatListRef.current?.scrollToIndex({index: 0, animated: false});
        }
      }

      return () => {
        // Cleanup code here will run when the screen is unfocused
      };
    }, [dataActivatedFirst.length]),
  );

  return (
    <View>
      <TitleLine
        title={`${brandRewardsCopy.exclusiveOffersTitle.toUpperCase()}`}
        onSeeAllPress={onSeeAllPress}
      />
      {variant === 'cart' && (
        <Text style={{marginBottom: 16}} color="black">
          Choose one of the offers below
        </Text>
      )}
      <FlatList
        ref={flatListRef}
        style={styles.flatlistStyle}
        horizontal
        data={dataActivatedFirst}
        renderItem={renderItem}
        keyExtractor={item => item.mflId}
        extraData={`${cartStore.activatedDeals.join(',')}_${cartStore.activeDealInvalid}`}
        ItemSeparatorComponent={Separator}
        showsHorizontalScrollIndicator={isWebDesktopScreen}
        {...visibleScrollbarProps(isWebDesktopScreen)}
        windowSize={3}
        maxToRenderPerBatch={3}
        initialNumToRender={3}
        removeClippedSubviews={true}
        contentContainerStyle={{
          maxWidth: dataActivatedFirst.length === 0 ? '100%' : undefined,
        }}
        ListEmptyComponent={
          <Text>
            There are no current {brandRewardsCopy.exclusiveOffersTitle}. Please check back later.
          </Text>
        }
      />
    </View>
  );
}

const useStyles = makeStyles((theme, {variant}: ExclusiveOffersProps) => {
  /**
   * cart variant needs negative margin to make up bottom margin for shadow
   */
  return {
    flatlistStyle: variant === 'cart' ? {marginBottom: -16} : {},
  };
});

function Separator() {
  return <View style={{width: 8, height: 8}} />;
}

const createRenderItem =
  (variant?: 'cart') =>
  ({item}: {item: OffersItemWithMflId}) => {
    return variant === 'cart' ? (
      <ExclusiveOffersTileCompact {...item} />
    ) : (
      <ExclusiveOffersTileLarge {...item} />
    );
  };

export default observer(ExclusiveOffers);
